<template>
  <View direction="column" gap="md">
    <div class="crop-area" @pointerdown="startDrag" @pointermove="onDrag" @pointerup="endDrag" @pointercancel="endDrag">
      <img
        ref="imageRef"
        :src="imageUrl"
        class="crop-image"
        :style="imageStyle"
        draggable="false"
        @load="onImageLoad"
      />
      <div class="crop-mask"></div>
    </div>

    <View align="center" gap="md">
      <Text variant="body-small" color="tertiary">Zoom</Text>
      <input v-model.number="zoom" class="zoom-slider" type="range" min="1" max="3" step="0.01" />
    </View>
  </View>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { Text, View } from '@tallo/design-system';

const props = defineProps<{ imageUrl: string }>();

// The crop circle is a fixed 180px window in a 240px-tall stage (mock 5b); the image pans/zooms under it
const cropSize = 180;

const imageRef = ref<HTMLImageElement>();
const zoom = ref(1);
const offsetX = ref(0);
const offsetY = ref(0);
const naturalWidth = ref(0);
const naturalHeight = ref(0);

const dragging = ref(false);
let dragStart = { x: 0, y: 0, offsetX: 0, offsetY: 0 };

// Base scale renders the image as "cover" for the crop circle; zoom multiplies on top
const baseScale = computed(() => {
  if (!naturalWidth.value || !naturalHeight.value) return 1;
  return Math.max(cropSize / naturalWidth.value, cropSize / naturalHeight.value);
});

const scale = computed(() => baseScale.value * zoom.value);

const imageStyle = computed(() => ({
  width: `${naturalWidth.value * scale.value}px`,
  height: `${naturalHeight.value * scale.value}px`,
  transform: `translate(calc(-50% + ${offsetX.value}px), calc(-50% + ${offsetY.value}px))`,
}));

function onImageLoad() {
  naturalWidth.value = imageRef.value?.naturalWidth ?? 0;
  naturalHeight.value = imageRef.value?.naturalHeight ?? 0;
  offsetX.value = 0;
  offsetY.value = 0;
  zoom.value = 1;
}

watch(zoom, () => clampOffsets());

function startDrag(event: PointerEvent) {
  dragging.value = true;
  (event.target as HTMLElement).setPointerCapture(event.pointerId);
  dragStart = { x: event.clientX, y: event.clientY, offsetX: offsetX.value, offsetY: offsetY.value };
}

function onDrag(event: PointerEvent) {
  if (!dragging.value) return;
  offsetX.value = dragStart.offsetX + (event.clientX - dragStart.x);
  offsetY.value = dragStart.offsetY + (event.clientY - dragStart.y);
  clampOffsets();
}

function endDrag() {
  dragging.value = false;
}

// The image may never leave a gap inside the crop circle
function clampOffsets() {
  const maxX = Math.max(0, (naturalWidth.value * scale.value - cropSize) / 2);
  const maxY = Math.max(0, (naturalHeight.value * scale.value - cropSize) / 2);
  offsetX.value = Math.min(maxX, Math.max(-maxX, offsetX.value));
  offsetY.value = Math.min(maxY, Math.max(-maxY, offsetY.value));
}

/** Renders the circle's content into a square PNG blob (output edge = `outputSize`) */
async function getCroppedBlob(outputSize = 480): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = outputSize;
  canvas.height = outputSize;
  const context = canvas.getContext('2d')!;

  const cropScale = outputSize / cropSize;
  const drawWidth = naturalWidth.value * scale.value * cropScale;
  const drawHeight = naturalHeight.value * scale.value * cropScale;
  const drawX = outputSize / 2 - drawWidth / 2 + offsetX.value * cropScale;
  const drawY = outputSize / 2 - drawHeight / 2 + offsetY.value * cropScale;

  context.drawImage(imageRef.value!, drawX, drawY, drawWidth, drawHeight);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not crop the image'))), 'image/png');
  });
}

defineExpose({ getCroppedBlob });
</script>

<style scoped lang="scss">
.crop-area {
  position: relative;
  height: 240px;
  border-radius: var(--t-border-radius-md);
  background: var(--t-color-neutral-950);
  overflow: hidden;
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
}

.crop-image {
  position: absolute;
  top: 50%;
  left: 50%;
  max-width: none;
  user-select: none;
  pointer-events: none;
}

.crop-mask {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 180px;
  height: 180px;
  transform: translate(-50%, -50%);
  border-radius: var(--t-border-radius-half-rounded);
  // Dims everything outside the circle without a second element
  box-shadow: 0 0 0 400px rgba(31, 32, 33, 0.55);
  pointer-events: none;
}

.zoom-slider {
  flex: 1;
  accent-color: var(--t-color-neutral-950);
}
</style>
